import { useState, KeyboardEvent } from 'react';
import { Popover, Box } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import BaseTextField from '@/components/form/BaseTextField';
import dayjs, { Dayjs } from 'dayjs';
import BaseButton from '../ui/BaseButton';
import { AccessTime, Check, Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const MIN_DATE_TIME = dayjs('1900-01-01');

interface DateRangePickerProps {
    label: string;
    startTime: Dayjs | null;
    endTime: Dayjs | null;
    onChange: (startTime: Dayjs | null, endTime: Dayjs | null) => void;
    onKeyUp?: (e: KeyboardEvent<Element>) => void;
}

export default function DateTimeRangePicker({ label, startTime, endTime, onChange, onKeyUp }: DateRangePickerProps) {
    const { t } = useTranslation('common');
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [tempStartTime, setTempStartTime] = useState<Dayjs | null>(startTime);
    const [tempEndTime, setTempEndTime] = useState<Dayjs | null>(endTime);
    const [validateMessage, setValidateMessage] = useState('');

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        // onChange(now.startOf('day'), now.endOf('day'));
        // Reset temp values when opening
        setTempStartTime(startTime);
        setTempEndTime(endTime);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const validate = () => {
        if (!tempStartTime && !tempEndTime) {
            setValidateMessage('');
            return true;
        } else if (!tempStartTime || !tempEndTime) {
            setValidateMessage(t('Please select both start and end times.'));
            return false;
        } else if (!tempStartTime?.isValid() || !tempEndTime?.isValid()) {
            setValidateMessage(t('Please select valid start and end times.'));
            return false;
        } else if (tempStartTime.isBefore(MIN_DATE_TIME) || tempEndTime.isBefore(MIN_DATE_TIME)) {
            setValidateMessage(t('Dates must be 1900-01-01 or later.'));
            return false;
        } else if (tempEndTime.isBefore(tempStartTime)) {
            setValidateMessage(t('End time cannot be before start time.'));
            return false;
        } else {
            setValidateMessage('');
            return true;
        }
    };

    const handleApply = () => {
        if (!validate()) return;
        onChange(tempStartTime, tempEndTime);
        handleClose();
    };

    const handleClear = () => {
        onChange(null, null);
        setValidateMessage('');
        handleClose();
    };

    const open = Boolean(anchorEl);

    // Format the date range as a string for display
    const dateRangeText =
        startTime || endTime
            ? `${startTime?.format('YYYY-MM-DD HH:mm') || ' '} - ${endTime?.format('YYYY-MM-DD HH:mm') || ' '}`
            : '';

    return (
        <>
            <BaseTextField
                label={label}
                placeholder={t('Date Range')}
                value={dateRangeText}
                onClick={handleClick}
                error={validateMessage !== ''}
                helperText={validateMessage}
                slotProps={{
                    input: {
                        readOnly: true,
                        endAdornment: <AccessTime />,
                    },
                }}
                onKeyUp={onKeyUp}
            />

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <DateTimePicker
                        label={t('Start Time')}
                        format="YYYY-MM-DD HH:mm"
                        ampm={false}
                        value={tempStartTime}
                        minDateTime={MIN_DATE_TIME}
                        maxDateTime={tempEndTime || undefined}
                        onChange={(value) => {
                            setTempStartTime(value);
                            if (!tempEndTime && value) setTempEndTime(value.hour(23).minute(59).second(59));
                        }}
                    />
                    <DateTimePicker
                        label={t('End Time')}
                        format="YYYY-MM-DD HH:mm"
                        ampm={false}
                        value={tempEndTime}
                        minDateTime={tempStartTime?.isAfter(MIN_DATE_TIME) ? tempStartTime : MIN_DATE_TIME}
                        onChange={(value) => {
                            setTempEndTime(value);
                        }}
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                        <BaseButton fullWidth startIcon={<Check />} onClick={handleApply}>
                            {t('Apply')}
                        </BaseButton>
                        <BaseButton fullWidth startIcon={<Close />} color="error" onClick={handleClear}>
                            {t('Clear')}
                        </BaseButton>
                    </Box>
                </Box>
            </Popover>
        </>
    );
}
